import { AvatarProps } from "@repo/ui/avatar";
import { KinsaInitialContext } from "src/lib/definitions";
import { ExternalMemberID } from "@repo/db/branded-types";

// List of available avatar variants (excluding special cases)
const VALID_AVATAR_VARIANTS = [
  "monkey",
  "cat",
  "unicorn",
  "shark",
  "penguin",
  "robot",
  "dinosaur",
  "dog",
] as const;

/**
 * Extracts and validates avatar variant from a profile image string
 * @param profileImage - The profile image string (e.g., "profile_icon_monkey" or "monkey")
 * @param fallbackId - ID to use for deterministic fallback if validation fails
 * @returns A valid avatar variant
 */
export const extractAvatarVariant = (
  profileImage: string | undefined,
  fallbackId: string
): Exclude<AvatarProps["variant"], "custom" | "unassignedDoc"> => {
  if (!profileImage) {
    return getAvatarFallback(fallbackId);
  }

  // Remove the 'profile_icon_' prefix if present
  const avatarVariant = profileImage.replace("profile_icon_", "");

  // Check if the avatar variant is valid
  if (
    VALID_AVATAR_VARIANTS.includes(
      avatarVariant as (typeof VALID_AVATAR_VARIANTS)[number]
    )
  ) {
    return avatarVariant as Exclude<
      AvatarProps["variant"],
      "custom" | "unassignedDoc"
    >;
  }

  // Fallback to deterministic generation
  return getAvatarFallback(fallbackId);
};

/**
 * Gets the patient's profile image from the coveredPersons array
 * Falls back to deterministic avatar generation if not found
 *
 * @param externalMemberId - ExternalMemberID that matches coveredPersons.id
 * @param coveredPersons - The coveredPersons array from the account
 */
export const getPatientAvatar = (
  externalMemberId: ExternalMemberID,
  coveredPersons: KinsaInitialContext["account"]["coveredPersons"]
): AvatarProps["variant"] => {
  // Find the patient in coveredPersons using the External Member ID
  const patient = coveredPersons.find(
    (person) => person.id === String(externalMemberId)
  );

  return extractAvatarVariant(
    patient?.custom?.profileImage,
    String(externalMemberId)
  );
};

/**
 * Deterministically maps a string to a valid avatar variant.
 * Uses a simple hash function to ensure consistent mapping.
 */
export const getAvatarFallback = (
  id: string
): Exclude<AvatarProps["variant"], "custom" | "unassignedDoc"> => {
  // Use the shared constant for avatar variants
  const avatarVariants = VALID_AVATAR_VARIANTS;

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Ensure positive index
  hash = Math.abs(hash);

  // Map hash to avatar variant
  return avatarVariants[hash % avatarVariants.length];
};
